import { inject, Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { firstValueFrom } from 'rxjs';
import { Logger } from 'src/app/core/helpers/logger';
import { ClassificationCode, IPaymentComponentOptionsDto, IPaymentDetailsDto, IPaymentOptionsDto, PaymentClassificationCodes } from '../contracts';
import { InternalError } from '../core/contracts';
import { HourlyData } from '../data/hourly.data';
import { MonthlyData } from '../data/monthly.data';
import { AdjustEventTokens, AdjustService } from './adjust.service';
import { AnalyticsService } from './analytics.service';
import { PaymentService } from './payment.service';

@Injectable({
  providedIn: 'root',
})
export class MarketingService {
  analytics = inject(AnalyticsService);
  options!: IPaymentComponentOptionsDto;
  private hourlyData = inject(HourlyData);
  private monthlyData = inject(MonthlyData);
  private logger = inject(Logger);
  adjust = inject(AdjustService);

  async logEvent(name: string, params?: any) {
    await this.analytics.init();

    // Firebase
    try {
      await FirebaseAnalytics.logEvent({ name, params });
    } catch (error) {
      this.logger.error({ msg: '@analytics.service: Error logging event', error, event: name });
    }

    // Adjust
    try {
      const adjustToken = (AdjustEventTokens as any)[name];
      if (adjustToken) {
        this.adjust.trackEvent(adjustToken);
      }
    } catch (error) {
      console.error('Adjust event log failed:', error);
    }
  }

  getPaymentDetails(requestId: number, classificationCode: ClassificationCode, options?: IPaymentOptionsDto) {
    if (PaymentService.isHourly(classificationCode)) {
      return this.hourlyData.getPaymentDetails(requestId, options);
    } else if (PaymentService.isMonthly(classificationCode)) {
      return this.monthlyData.getPaymentDetails(requestId, options);
    } else if (PaymentService.isInstallment(classificationCode)) {
      return this.monthlyData.getInstallmentPaymentDetails(requestId, options);
    } else if (PaymentService.isDelayRequest(classificationCode)) {
      return this.hourlyData.getDelayRequestPaymentDetails(requestId, options);
    } else {
      throw new InternalError(`Payment classification is not supported: '${classificationCode}'`, 'UNSUPPORTED_SERVICE_TYPE');
    }
  }

  public async logPurchase(requestId: number, classificationCode: ClassificationCode) {
    const options: IPaymentOptionsDto = { useWallet: false };

    try {
      const details: IPaymentDetailsDto = await firstValueFrom(this.getPaymentDetails(requestId, classificationCode, options));

      if (!details) {
        console.warn('[MARKETING] Payment details missing, skipping marketing event');
        return;
      }

      const amount = details.amountToPay ?? 0;
      const discount = details.discountAmount ?? 0;

      const itemName = PaymentService.isHourly(classificationCode)
        ? PaymentClassificationCodes.Khidma
        : PaymentService.isMonthly(classificationCode)
        ? PaymentClassificationCodes.Monthly
        : PaymentService.isInstallment(classificationCode)
        ? PaymentClassificationCodes.Installment
        : PaymentService.isDelayRequest(classificationCode)
        ? PaymentClassificationCodes.DelayRequest
        : 'Unknown';

      const eventParams: any = {
        value: amount,
        currency: 'SAR',
        item_id: `${classificationCode}${requestId}`,
        item_name: itemName,
        discount: discount,
      };
      // Firebase Analytics
      try {
        await FirebaseAnalytics.logEvent({
          name: 'purchase',
          params: eventParams,
        });
      } catch (err) {
        console.error('[MARKETING] FirebaseAnalytics purchase event FAILED', err);
      }

      // Adjust
      try {
        this.adjust.trackEvent(AdjustEventTokens.Purchase);
      } catch (err) {
        console.error('[MARKETING] Adjust purchase event FAILED', err);
      }
    } catch (err) {
      console.error('[MARKETING] Error getting payment details:', err);
    }
  }
}
